import React, { useState, useEffect } from 'react';
import {
  getAlerts, getUnreadCount, markRead, markAllRead, clearAlerts,
  subscribeAlerts, Alert, AlertSeverity
} from './alertStore';
import { AlertQualityPanel } from '../71b/AlertQualityPanel';

// ─── Severity config ──────────────────────────────────────────────────────────

const SEV_CFG: Record<AlertSeverity, { color: string; bg: string; bd: string; icon: string }> = {
  info:         { color: 'var(--blue-muted)', bg: 'var(--surface2)', bd: 'var(--border)', icon: '○' },
  warning:      { color: 'var(--amber)',      bg: 'var(--amber-bg)', bd: 'var(--amber-bd)', icon: '⚠' },
  actionable:   { color: 'var(--green)',      bg: 'var(--green-bg)', bd: 'var(--green-bd)', icon: '⚡' },
  invalidation: { color: 'var(--red)',        bg: 'var(--red-bg)',   bd: 'var(--red-bd)',   icon: '✕' },
};

// ─── Single alert row ─────────────────────────────────────────────────────────

function AlertRow({ alert, onRead, onSymbolSelect }: { alert: Alert; onRead: () => void; onSymbolSelect?: (s: string) => void }) {
  const cfg = SEV_CFG[alert.severity];
  const age = formatAge(alert.timestamp);

  return (
    <div
      onClick={onRead}
      style={{
        display: 'flex', gap: 8, padding: '7px 10px',
        background: alert.read ? 'transparent' : cfg.bg,
        borderLeft: `3px solid ${alert.read ? 'var(--border)' : cfg.color}`,
        borderRadius: '0 4px 4px 0',
        marginBottom: 3,
        cursor: 'pointer',
        transition: 'background .15s',
        opacity: alert.read ? .6 : 1,
      }}
    >
      <span style={{ fontSize: 11, color: cfg.color, flexShrink: 0, fontFamily: 'var(--mono)' }}>
        {cfg.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, marginBottom: 1 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: alert.read ? 'var(--blue-dim)' : 'var(--text)', fontFamily: 'var(--mono)' }}>
            {alert.symbol && (
              <span
                onClick={e => { e.stopPropagation(); onSymbolSelect?.(alert.symbol!); }}
                style={{ color: cfg.color, marginRight: 5, cursor: onSymbolSelect ? 'pointer' : 'default',
                  textDecoration: onSymbolSelect ? 'underline' : 'none', textDecorationStyle: 'dotted' }}
                title={onSymbolSelect ? `Switch to ${alert.symbol}` : undefined}
              >{alert.symbol}</span>
            )}
            {alert.title}
          </span>
          <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', flexShrink: 0 }}>{age}</span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--blue-muted)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {alert.message}
        </div>
      </div>
    </div>
  );
}

function formatAge(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60)   return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  return `${Math.floor(s/3600)}h`;
}

// ─── Main AlertStream ─────────────────────────────────────────────────────────

interface AlertStreamProps {
  symbol?:         string;    // filter to symbol (undefined = all)
  maxHeight?:      number;
  compact?:        boolean;   // compact mode for dashboard embed
  onSymbolSelect?: (symbol: string) => void;  // click alert -> switch to symbol
}

export function AlertStream({ symbol, maxHeight = 320, compact = false, onSymbolSelect }: AlertStreamProps) {
  const [, rerender]  = useState(0);
  const [filter, setFilter] = useState<AlertSeverity | 'all'>('all');
  const [symFilter] = useState<string>('all');
  const [showQuality, setShowQuality] = useState(false);

  useEffect(() => {
    const unsub = subscribeAlerts(() => rerender(n => n + 1));
    // Tick every 10s to refresh age labels
    const tick = setInterval(() => rerender(n => n + 1), 10_000);
    return () => { unsub(); clearInterval(tick); };
  }, []);

  const allAlerts = getAlerts({
    symbol:     symbol !== undefined ? symbol : symFilter !== 'all' ? symFilter : undefined,
    severity:   filter !== 'all' ? filter : undefined,
    limit:      compact ? 12 : 50,
  });

  const unread = getUnreadCount(symbol);

  if (compact) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="sl" style={{ marginBottom: 0 }}>Alerts</span>
            {unread > 0 && (
              <span style={{
                fontSize: 9, fontFamily: 'var(--mono)', fontWeight: 700,
                color: 'var(--navy)', background: 'var(--red)',
                padding: '1px 5px', borderRadius: 10,
              }}>{unread}</span>
            )}
          </div>
          {unread > 0 && (
            <button
              onClick={() => markAllRead(symbol)}
              style={{ fontSize: 9, color: 'var(--blue-dim)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mono)' }}
            >Mark all read</button>
          )}
        </div>
        <div style={{ maxHeight: maxHeight, overflowY: 'auto' }}>
          {allAlerts.length === 0 ? (
            <div style={{ fontSize: 10, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', textAlign: 'center', padding: '12px 0' }}>
              NO ALERTS
            </div>
          ) : (
            allAlerts.map(a => <AlertRow key={a.id} alert={a} onRead={() => markRead(a.id)} onSymbolSelect={onSymbolSelect} />)
          )}
        </div>
      </div>
    );
  }

  // Full panel
  const sevOptions: (AlertSeverity | 'all')[] = ['all', 'actionable', 'warning', 'invalidation', 'info'];

  return (
    <div className="card">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="sl" style={{ marginBottom: 0 }}>Alert Stream</div>
          {unread > 0 && (
            <span style={{
              fontSize: 9, fontFamily: 'var(--mono)', fontWeight: 700,
              color: 'var(--navy)', background: 'var(--red)',
              padding: '1px 7px', borderRadius: 10,
            }}>{unread} new</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {/* Phase 7.1B — quality view toggle */}
          <button
            onClick={() => setShowQuality(p => !p)}
            className="btn btn-sm"
            style={{
              fontSize: 9, fontFamily: 'var(--mono)',
              color: showQuality ? 'var(--gold)' : 'var(--blue-dim)',
              background: showQuality ? 'var(--gold)12' : undefined,
              border: showQuality ? '1px solid var(--gold)55' : undefined,
            }}
          >{showQuality ? '▲ Quality' : '▼ Quality'}</button>
          <button
            onClick={() => { markAllRead(symbol); }}
            className="btn btn-sm"
            style={{ fontSize: 9, fontFamily: 'var(--mono)' }}
          >Mark all read</button>
          <button
            onClick={() => clearAlerts(symbol)}
            className="btn btn-sm"
            style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--red)' }}
          >Clear</button>
        </div>
      </div>

      {/* Phase 7.1B — Alert Quality Panel (presentation-only) */}
      {showQuality && (
        <div style={{ marginBottom: 12 }}>
          <AlertQualityPanel />
        </div>
      )}

      {/* Severity filter */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {sevOptions.map(s => {
          const cfg = s !== 'all' ? SEV_CFG[s] : null;
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                fontSize: 9, fontFamily: 'var(--mono)', fontWeight: 700,
                padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
                border: `1px solid ${filter === s ? (cfg?.color || 'var(--gold)') : 'var(--border)'}`,
                background: filter === s ? (cfg ? cfg.bg : 'var(--gold-dim)') : 'transparent',
                color: filter === s ? (cfg?.color || 'var(--gold)') : 'var(--blue-dim)',
                letterSpacing: '.5px', textTransform: 'uppercase',
              }}
            >{s}</button>
          );
        })}
      </div>

      {/* Alert list */}
      <div style={{ maxHeight: maxHeight, overflowY: 'auto' }}>
        {allAlerts.length === 0 ? (
          <div className="empty" style={{ padding: '20px' }}>
            <div className="icon">◎</div>
            <h3>NO ALERTS</h3>
            <p>Alerts appear here when significant market events occur.</p>
          </div>
        ) : (
          allAlerts.map(a => <AlertRow key={a.id} alert={a} onRead={() => markRead(a.id)} onSymbolSelect={onSymbolSelect} />)
        )}
      </div>
    </div>
  );
}

// ─── Alert badge (for tab indicators) ─────────────────────────────────────────

export function AlertBadge({ symbol }: { symbol?: string }) {
  const [count, setCount] = useState(getUnreadCount(symbol));
  useEffect(() => {
    const unsub = subscribeAlerts(() => setCount(getUnreadCount(symbol)));
    return unsub;
  }, [symbol]);
  if (!count) return null;
  return (
    <span style={{
      fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
      color: 'var(--navy)', background: 'var(--red)',
      padding: '0 4px', borderRadius: 8, marginLeft: 4,
    }}>{count}</span>
  );
}
